// userActions.tsx · v12.10.26 (#2132 Resend says what went per channel and why not; #2133 Copy invite link) · 2026-10-30 · VoiceSpectrum AI
// userActions.tsx · v12.10.25 (#2106 Show welcome again) · 2026-10-30 · VoiceSpectrum AI
// VoiceSpectrum.Web.T2 · v12.10.9.1 (#1292 Unlock shows why the account locked first) · 2026-10-06
// VoiceSpectrum.Web.T2 · v12.10.9 (Security Phase 1 – #1258/#1259/#1260/#1261) · 2026-10-06
// userActions.tsx - the person actions shared by the Users ⋮ menu and the person drawer (prototype scUserMenu / scPwReset /
// scReset2fa / scDisable / scEnable / scDelete / scResend / scRevoke). Each opens the right confirmation (§23), calls the
// contract endpoint through withConfirm where the server asks "Confirm it's you", and reports the result.
// Life cycle (S37): Invited -> Active (Locked = temporary) -> Disabled -> Deleted (only from Disabled, typed name).
import { isConfirmCancelled, withConfirm } from '../../components/security/ConfirmItsYou';
import { displayName, identity, isLastManager, methodName, requiredFor } from '../../components/security/people';
import type { ConfirmOpts } from '../../components/security/ui';
import { securityApi, securityErrorMessage, type Group, type InviteDelivery, type LockInfo, type Policy, type UserRow } from '../../lib/securityApi';
import { showInviteLink } from '../../components/security/InviteLinkModal';   // v12.10.26 (#2133)
import { toast } from '../../lib/toast';

export interface UserActionCtx {
  users: UserRow[];
  groups: Group[];
  policy: Policy | null;
  ask: (o: ConfirmOpts) => void;
  /** a person changed (row = the fresh row when the API returned one) or was removed - refresh the list */
  onChanged: (id: number, row: UserRow | null, removed?: boolean) => void;
}
export type UserActions = ReturnType<typeof makeUserActions>;

const quiet = (e: unknown) => (isConfirmCancelled(e) ? null : securityErrorMessage(e));
function firstName(u: UserRow): string { return (u.firstName || displayName(u)).split(' ')[0]; }

/** v12.10.26 (#2132 #2129): one toast that says exactly what an invite send did - "(email) (text)", what didn't go and why. */
export function deliveryToast(who: string, d: InviteDelivery | undefined, verb = 'Invite sent'): void {
  if (!d) { toast('success', `${verb} to ${who}`, 'The new link works for 7 days.'); return; }
  const went = [d.emailSent ? 'email' : '', d.textSent ? 'text' : ''].filter(Boolean).join(' + ');
  if (d.emailSent || d.textSent) {
    if ((d.emailTried && !d.emailSent) || (d.textTried && !d.textSent))
      toast('warning', `${verb} to ${who} by ${went} only`, `${d.problem ?? ''} The new link works for 7 days.`.trim());
    else toast('success', `${verb} to ${who} (${went})`, 'The new link works for 7 days; any earlier link no longer works.');
    return;
  }
  toast('error', `Invite not sent to ${who}`, `${d.problem ?? 'Nothing could be sent.'} The earlier link no longer works - fix this and resend, or use Copy invite link.`);
}
/** v12.10.9.1 (#1292): "Chrome on Windows" from a user-agent string (best effort, no library). */
export function browserName(ua: string | null | undefined): string {
  if (!ua) return 'an unknown browser';
  const b = /Edg\//.test(ua) ? 'Edge' : /OPR\//.test(ua) ? 'Opera' : /Chrome\//.test(ua) ? 'Chrome' : /Firefox\//.test(ua) ? 'Firefox' : /Safari\//.test(ua) ? 'Safari' : 'a browser';
  const os = /iPhone|iPad/.test(ua) ? 'iPhone / iPad' : /Android/.test(ua) ? 'Android' : /Windows/.test(ua) ? 'Windows' : /Mac OS X/.test(ua) ? 'Mac' : /Linux/.test(ua) ? 'Linux' : '';
  return os ? `${b} on ${os}` : b;
}
function when(iso: string | null | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  const today = new Date().toDateString() === d.toDateString();
  return today ? d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' }) : d.toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
}
function lockBody(name: string, info: LockInfo | null) {
  if (!info) return <>Unlocking lets <b>{name}</b> try to sign in again right away. Their password and 2-step don’t change.</>;
  const ends = info.lockedUntil ? when(info.lockedUntil) : '';
  return (
    <>
      <p style={{ margin: '0 0 8px' }}>
        <b>{name}</b> was locked{info.lockedAt ? <> at <b>{when(info.lockedAt)}</b></> : null}{info.reason ? <> after <b>{info.reason}</b></> : null}.
        {ends ? <> The lock ends by itself at <b>{ends}</b>.</> : null}
      </p>
      {info.attempts.length > 0 && (
        <>
          <div style={{ fontSize: 12, fontWeight: 700, color: '#5b6680', margin: '10px 0 4px' }}>LAST ATTEMPTS</div>
          <ul style={{ margin: 0, paddingLeft: 18, fontSize: 13 }}>
            {info.attempts.slice(0, 5).map((a, i) => <li key={i}>{when(a.at)} · {a.what} · {browserName(a.userAgent)}</li>)}
          </ul>
        </>
      )}
      <p style={{ margin: '10px 0 0', fontSize: 13, color: '#5b6680' }}>If these attempts don’t look like {name.split(' ')[0]}, send a password reset instead of unlocking.</p>
    </>
  );
}

export function makeUserActions(ctx: UserActionCtx) {
  const { ask, onChanged } = ctx;

  async function run(fn: () => Promise<unknown>, okTitle: string, okMsg?: string): Promise<boolean> {
    try { await fn(); toast('success', okTitle, okMsg); return true; }
    catch (e) { if (!isConfirmCancelled(e)) toast('error', 'That didn’t work', securityErrorMessage(e)); return false; }
  }

  function disable(u: UserRow) {
    const name = displayName(u);
    if (isLastManager(u, ctx.users, ctx.groups)) {
      ask({ sev: 'warn', title: 'Can’t disable the last person who manages users', body: `${name} is the only active person whose group can manage users and groups. Give that to someone else first.`, label: 'OK', noCancel: true, onOk: () => undefined });
      return;
    }
    ask({
      sev: 'warn', title: `Disable ${name}?`, label: 'Disable user', danger: true,
      body: 'They’re signed out now and can’t sign in. Their messages, notes and history stay, and you can enable them again at any time.',
      onOk: async () => {
        const row = await withConfirm(() => securityApi.disable(u.id), `You’re disabling ${name}.`);
        onChanged(u.id, row ?? null);
        toast('success', `${name} disabled`);
      },
      errorText: quiet,
    });
  }

  return {
    pwReset(u: UserRow) {
      const name = displayName(u);
      ask({
        sev: 'info', title: 'Send a password reset link?', label: 'Send link',
        body: u.email
          ? <><b>{name}</b> gets an email at {u.email} with a link to choose a new password. Their current password keeps working until they do. You never see or set it.</>
          : <><b>{name}</b> gets a text at {u.mobile} with a code to choose a new password. Their current password keeps working until they do. You never see or set it.</>,
        onOk: async () => { await securityApi.sendPasswordReset(u.id); toast('success', u.email ? `Reset link sent to ${u.email}` : `Reset code sent to ${u.mobile}`); },
        errorText: quiet,
      });
    },
    reset2(u: UserRow) {
      const name = displayName(u);
      const m = (u.methods ?? []).map(methodName);
      const must = requiredFor(u, ctx.policy, ctx.groups);
      ask({
        sev: 'warn', title: `Reset ${firstName(u)}’s 2-step verification?`, label: 'Reset 2-step verification', danger: true,
        body: <>This removes <b>{m.length ? m.join(' and ') : 'their second step'}</b> and any unused backup codes. At their next sign-in they’ll set up a new method{must ? ' before they can get in' : ''}. This is recorded in the audit log.</>,
        onOk: async () => {
          const row = await withConfirm(() => securityApi.resetSecondStep(u.id), `You’re resetting ${name}’s 2-step verification.`);
          onChanged(u.id, row ?? null);
          toast('success', `2-step verification reset for ${name}`);
        },
        errorText: quiet,
      });
    },
    // v12.10.9.1 (#1292, Tushar 10/6): a window says what happened and why it is locked, then Unlock or Cancel.
    async unlock(u: UserRow) {
      const name = displayName(u);
      let info: LockInfo | null = null;
      try { info = await securityApi.lockInfo(u.id); } catch { /* older API - the window still asks */ }
      ask({
        sev: 'warn', title: `Unlock ${name}?`, label: 'Unlock',
        body: lockBody(name, info),
        onOk: async () => {
          const row = await securityApi.unlock(u.id);
          onChanged(u.id, row ?? null);
          toast('success', `${name} unlocked`);
        },
        errorText: quiet,
      });
    },
    // v12.10.25 (#2106): Show welcome again - the person gets the first-run Welcome / guides as new (audited on the server).
    showWelcomeAgain(u: UserRow) {
      const name = displayName(u);
      ask({
        sev: 'info', title: `Show the welcome to ${firstName(u)} again?`, label: 'Show welcome again',
        body: <><b>{name}</b> sees the Welcome card and the getting-started guides again as if new. If they have signed in more than five times, the guides wait in Help › Get familiar instead of opening by themselves.</>,
        onOk: async () => { await securityApi.resetFirstRun(u.id); toast('success', `Welcome reset for ${name}`); },
        errorText: quiet,
      });
    },
    async signOut(u: UserRow) {
      await run(() => securityApi.signOutUser(u.id), `${displayName(u)} signed out everywhere`, 'They sign in again at their next click.');
    },
    disable,
    enable(u: UserRow) {
      const name = displayName(u);
      ask({
        sev: 'info', title: `Enable ${name}?`, label: 'Enable user',
        body: 'They can sign in again with their existing password and 2-step verification. Their groups are unchanged. Someone disabled for more than 90 days chooses a new password at their next sign-in.',
        onOk: async () => {
          const row = await withConfirm(() => securityApi.enable(u.id), `You’re enabling ${name}.`);
          onChanged(u.id, row ?? null);
          toast('success', `${name} enabled`);
        },
        errorText: quiet,
      });
    },
    del(u: UserRow, afterDelete?: () => void) {
      if (u.status !== 'disabled') { disable(u); return; }
      const name = displayName(u);
      const typed = `${u.firstName ?? ''} ${u.lastName ?? ''}`.trim() || name;
      ask({
        sev: 'bad', title: `Delete ${name}?`, label: 'Delete user', danger: true, typed,
        body: <>This can’t be undone. Their sign-in is removed and their email and phone become free to use again. Past messages, notes and audit entries keep their name as “{name} (deleted)”.</>,
        onOk: async () => {
          await withConfirm(() => securityApi.deleteUser(u.id), `You’re deleting ${name}.`);
          onChanged(u.id, null, true);
          afterDelete?.();
          toast('success', `${name} deleted`);
        },
        errorText: quiet,
      });
    },
    // v12.10.26 (#2132): the answer says per channel what went (email always, plus text when a mobile is on file) - and why not.
    async resend(u: UserRow) {
      try {
        const r = await securityApi.resendInvite(u.id);
        deliveryToast(displayName(u) || identity(u), r?.delivery, 'Invite resent');
        onChanged(u.id, r ?? null);
      } catch (e) { if (!isConfirmCancelled(e)) toast('error', 'Invite not resent', securityErrorMessage(e)); }
    },
    // v12.10.26 (#2133): Copy invite link - a fresh one-time link (the earlier one stops working), shown once; no confirm step (decided 10/30).
    async copyInviteLink(u: UserRow) {
      try {
        const r = await securityApi.inviteLink(u.id);
        showInviteLink({ name: displayName(u) || identity(u), link: r.link, expiresAt: r.expiresAt });
        onChanged(u.id, r.row ?? null);
      } catch (e) { if (!isConfirmCancelled(e)) toast('error', 'No invite link made', securityErrorMessage(e)); }
    },
    cancelInvite(u: UserRow, afterCancel?: () => void) {
      ask({
        sev: 'warn', title: 'Cancel this invite?', label: 'Cancel invite', danger: true, cancelLabel: 'Keep invite',
        body: <>The link sent to <b>{identity(u)}</b> stops working. You can invite them again later.</>,
        onOk: async () => {
          await securityApi.cancelInvite(u.id);
          onChanged(u.id, null, true);
          afterCancel?.();
          toast('success', 'Invite cancelled');
        },
        errorText: quiet,
      });
    },
  };
}
